import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, Button,
  Container, Alert, CircularProgress, List,
  ListItemButton, ListItemText, Divider, Chip,
} from '@mui/material';
import { useAuthStore } from '../stores/authStore';
import { GOOGLE_CLIENT_ID, GOOGLE_REDIRECT_URI } from '../config/api';

function buildGoogleOAuthUrl(): string {
  const params = new URLSearchParams({
    client_id: GOOGLE_CLIENT_ID,
    redirect_uri: GOOGLE_REDIRECT_URI,
    response_type: 'code',
    scope: 'openid email profile',
    access_type: 'offline',
    prompt: 'select_account',
  });
  return `https://accounts.google.com/o/oauth2/v2/auth?${params}`;
}

const LoginPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const oauthError = searchParams.get('error');
  const {
    login, selectSite, isLoading, error, clearError,
    requiresSiteSelection, availableSites,
  } = useAuthStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    await login(email, password);
    // If single site, store will set isAuthenticated and we redirect
    if (useAuthStore.getState().isAuthenticated) navigate('/');
  };

  const handleSiteSelect = async (siteId: number) => {
    clearError();
    await selectSite(siteId);
    if (useAuthStore.getState().isAuthenticated) navigate('/');
  };

  // ── Site selection screen ────────────────────────────────────────────────

  if (requiresSiteSelection) {
    return (
      <Container component="main" maxWidth="xs">
        <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
            <Typography component="h1" variant="h5" align="center" gutterBottom>
              Select Clinic
            </Typography>
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
              Your account has access to multiple clinics. Choose one to continue.
            </Typography>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            <List disablePadding>
              {availableSites.map((site, idx) => (
                <Box key={site.id}>
                  {idx > 0 && <Divider />}
                  <ListItemButton
                    onClick={() => handleSiteSelect(site.id)}
                    disabled={isLoading}
                    sx={{ borderRadius: 1, my: 0.5 }}
                  >
                    <ListItemText
                      primary={site.name}
                      secondary={site.subdomain ? `${site.subdomain}.sw-vet-clinic.com` : undefined}
                    />
                    {site.is_default && <Chip label="Default" size="small" color="primary" />}
                  </ListItemButton>
                </Box>
              ))}
            </List>

            {isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}
          </Paper>
        </Box>
      </Container>
    );
  }

  // ── Login screen ─────────────────────────────────────────────────────────

  return (
    <Container component="main" maxWidth="xs">
      <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ padding: 4, width: '100%' }}>
          <Typography component="h1" variant="h4" align="center" gutterBottom>
            SW Vet Clinic
          </Typography>
          <Typography component="h2" variant="h6" align="center" color="text.secondary" sx={{ mb: 3 }}>
            Sign in to your account
          </Typography>

          {(error || oauthError) && (
            <Alert severity="error" sx={{ mb: 2 }} onClose={clearError}>
              {error || oauthError}
            </Alert>
          )}

          {/* Google Sign-In */}
          {GOOGLE_CLIENT_ID && (
            <>
              <Button
                fullWidth
                variant="outlined"
                size="large"
                onClick={() => { window.location.href = buildGoogleOAuthUrl(); }}
                disabled={isLoading}
                startIcon={
                  <img src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                    alt="" width={20} height={20} />
                }
                sx={{ mb: 2 }}
              >
                Continue with Google
              </Button>
              <Divider sx={{ mb: 2 }}>or</Divider>
            </>
          )}

          <Box component="form" onSubmit={handleLogin}>
            <TextField
              margin="normal"
              required
              fullWidth
              label="Email Address"
              type="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isLoading}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              label="Password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isLoading}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              sx={{ mt: 3, mb: 2 }}
              disabled={isLoading || !email || !password}
            >
              {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Sign In'}
            </Button>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default LoginPage;
